'use client';

import { AdminProfile, DashboardStats } from '../../types';
import AdminHeader from '../shared/AdminHeader';
import AdminNav from '../shared/AdminNav';

interface DashboardPageProps {
  profile: AdminProfile;
  stats: DashboardStats;
  onNavigate: (page: 'dashboard' | 'receipts' | 'clients' | 'users' | 'settings') => void;
  onLogout: () => void;
  onGoToKiosk: () => void;
  onViewAllReceipts: () => void;
}

export default function DashboardPage({
  profile,
  stats,
  onNavigate,
  onLogout,
  onGoToKiosk,
  onViewAllReceipts,
}: DashboardPageProps) {
  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader profile={profile} onLogout={onLogout} onGoToKiosk={onGoToKiosk} />
      <AdminNav
        currentPage="dashboard"
        onNavigate={onNavigate}
        isSuperAdmin={profile.role === 'super_admin'}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">오늘 전표</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.todayCount}건
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">오늘 매출</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.todayAmount.toLocaleString()}원
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">이번달 전표</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.monthCount}건
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">이번달 매출</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.monthAmount.toLocaleString()}원
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Receipts */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              최근 전표
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              최근 생성된 전표 5개
            </p>
          </div>
          <div className="border-t border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    전표번호
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    화주명
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    수량
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    생성일시
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.recentReceipts.map((receipt) => (
                  <tr key={receipt.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {receipt.receipt_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {receipt.shipper_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {receipt.pallet_quantity} {receipt.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {receipt.total_amount?.toLocaleString()}원
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(receipt.created_at).toLocaleString('ko-KR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <button
              onClick={onViewAllReceipts}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              전체 전표 보기
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
